import React from 'react'
import moment from 'moment'
import {
  Card, CardBody, CardTitle, Row, Col, Spinner, Container,
} from 'reactstrap'
import { Doughnut } from 'react-chartjs-2'
import { ChartData } from 'chart.js'
import { gql, useQuery } from '@apollo/client'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { chartColors } from 'components/charts/colors'
import { Query, UserAnswers } from 'generated/types'
import { useFilters } from './FiltersContext'

const SYMPTOMS = gql`
  query Symptoms($tags: [String!], $startDate: DateTime!, $endDate: DateTime!) {
    questionBySlug(slug: "symptoms-check") {
      id
      slug
      mostRecentResponses(filters: { tags: $tags, startDate: $startDate, endDate: $endDate }) {
        answers {
          id
          option {
            id
            slug
          }
        }
      }
    }
  }
`

const generateConfigWithData = (data: number[]) => {
  const config: ChartData = {
    datasets: [
      {
        data,
        backgroundColor: [chartColors.good, chartColors.lessGood, chartColors.bad],
        borderWidth: 0,
      },
    ],
    labels: ['No Symptoms', '1 Symptom', '2+ Symptoms'],
  }
  return config
}

const symptomsToChartData = (symptoms: UserAnswers[]) => {
  /* es-lint-disable no-param-reassign */
  if (symptoms.length === 0) {
    return generateConfigWithData([])
  }

  const symptomCategories = symptoms.reduce(
    (totals, s) => {
      const newTotals = totals
      switch (s.answers.length) {
        case 0:
          // nop
          break
        case 1:
          if (s.answers[0].option?.slug === 'no-symptoms') {
            newTotals.noSymptoms += 1 // es-lint-disable-line no-param-reassign
          } else {
            newTotals.oneSymptom += 1 // es-lint-disable-line no-param-reassign
          }
          break
        default:
          newTotals.twoPlusSymptoms += 1 // es-lint-disable-line no-param-reassign
      }

      return newTotals // es-lint-disable-line no-param-reassign
    },
    {
      noSymptoms: 0,
      oneSymptom: 0,
      twoPlusSymptoms: 0,
    },
  )

  return generateConfigWithData([
    symptomCategories.noSymptoms,
    symptomCategories.oneSymptom,
    symptomCategories.twoPlusSymptoms,
  ])
}

const SymptomsLastTwoWeeks = () => {
  const today = moment().startOf('day')
  const mostRecentWeekStart = today.clone().subtract(7, 'days')
  const previousWeekStart = mostRecentWeekStart.clone().subtract(7, 'days')

  const [filters] = useFilters()

  interface WeekChartProps {
    title: string
    startDate: moment.Moment
    endDate: moment.Moment
  }

  const WeekChart: React.FC<WeekChartProps> = ({ title, startDate, endDate }) => {
    const symptoms = useQuery<Query>(SYMPTOMS, {
      variables: {
        tags: filters.tags,
        startDate: startDate.toDate(),
        endDate: endDate.toDate(),
      },
    })

    if (symptoms.loading) {
      return <Spinner />
    }

    if (symptoms.error) {
      console.error('error loading symptoms data:', symptoms.error)
      return (
        <>
          <div className="d-flex justify-content-center">
            <FontAwesomeIcon icon="exclamation-triangle" color={chartColors.bad} transform="grow-30" className="mt-3" />
          </div>
          <div className="mt-4 text-center fs--1 text-white">
            <p>There was an error loading symptom reports</p>
          </div>
        </>
      )
    }

    return (
      <Card className="mb-3">
        <CardBody>
          <CardTitle>{title}</CardTitle>
          <Doughnut
            data={symptomsToChartData(symptoms.data!.questionBySlug.mostRecentResponses)}
            options={{
              legend: {
                position: 'right',
              },
            }}
          />
        </CardBody>
      </Card>
    )
  }

  return (
    <Card className="mb-3">
      <CardBody className="rounded-soft">
        <CardTitle>
          <h4 className="mb-1">Symptoms</h4>
        </CardTitle>
        <Container>
          <Row>
            <Col sm="12" md="6">
              <WeekChart title="Previous Week" startDate={previousWeekStart} endDate={mostRecentWeekStart} />
            </Col>
            <Col sm="12" md="6">
              <WeekChart title="Most Recent Week" startDate={mostRecentWeekStart} endDate={today} />
            </Col>
          </Row>
        </Container>
      </CardBody>
    </Card>
  )
}

export default SymptomsLastTwoWeeks
